import { useState, useEffect } from "react";

const fmt = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export const BookingCard = ({ sessionId, api, defaultName, defaultEmail }) => {
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: defaultName || "", email: defaultEmail || "" });
  const [done, setDone] = useState(null);
  const [cal, setCal] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${api}/booking/slots`).then((r) => r.json()).then((d) => setSlots(d.slots)).catch(() => {});
  }, [api]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected || !form.name || !form.email) return;
    setBusy(true);
    try {
      const res = await fetch(`${api}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId, slot: selected, ...form,
          attribution: (() => { try { return JSON.parse(localStorage.getItem("dl_attrib")); } catch { return null; } })(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409) {
          setSelected(null);
          fetch(`${api}/booking/slots`).then((r) => r.json()).then((d) => setSlots(d.slots)).catch(() => {});
        }
        throw new Error(typeof data.detail === "string" ? data.detail : "Booking failed");
      }
      setDone(data.message);
      setCal(data.calendar || null);
    } catch (e) {
      setError(e.message || "The booking didn't go through — please try again.");
    }
    setBusy(false);
  };

  const field = "rounded-xl bg-[#050505] border border-[#2E2E2E] px-4 py-3 text-base font-medium text-white placeholder:text-[#D6D6D6] outline-none focus:border-[#FF5C5C] w-full transition-colors duration-150";

  if (done) {
    return (
      <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6 max-w-2xl space-y-4" data-testid="booking-success">
        <p className="text-base font-medium text-white leading-[1.6]">{done}</p>
        {cal && (
          <div className="flex flex-wrap gap-3">
            <a href={cal.gcal_url} target="_blank" rel="noreferrer" data-testid="booking-gcal-link"
              className="flex items-center gap-2 rounded-xl bg-white text-black font-semibold text-base px-4 py-2.5 hover:bg-[#D6D6D6] transition-colors duration-150">
              Add to Google Calendar
            </a>
            <a href={`${api}/calendar/invite/${cal.ics_token}.ics`} data-testid="booking-ics-link"
              className="flex items-center gap-2 rounded-xl border border-[#2E2E2E] text-[#D6D6D6] font-semibold text-base px-4 py-2.5 hover:text-white transition-colors duration-150">
              Any other calendar (.ics)
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="relative overflow-hidden rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6 md:p-8 max-w-2xl space-y-4" data-testid="booking-card">
      <div className="absolute top-0 left-0 w-full h-1 bg-[#DC2626]" />
      <p className="text-base font-bold text-[#FF5C5C] pt-1">Book a call</p>
      <p className="text-base font-medium text-[#D6D6D6] leading-[1.6]">Pick a time that works for you.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto chat-scroll">
        {slots.map((s) => (
          <button key={s} type="button" onClick={() => setSelected(s)} data-testid="booking-slot"
            className={`rounded-xl text-base font-medium px-4 py-3 border text-left transition-colors duration-150 ${selected === s ? "bg-[#DC2626] text-white border-[#DC2626]" : "border-[#2E2E2E] text-[#D6D6D6] hover:border-[#FF5C5C]"}`}>
            {fmt(s)}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input className={field} placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name-input" />
        <input className={field} type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email-input" />
      </div>
      {error && <p className="text-base font-semibold text-[#FF5C5C]" data-testid="booking-error">{error}</p>}
      <button type="submit" disabled={busy || !selected || !form.name || !form.email} data-testid="booking-submit-button"
        className="rounded-xl bg-white text-black font-semibold text-base px-8 py-3 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150 w-full sm:w-auto">
        {busy ? "Confirming..." : "Confirm this time"}
      </button>
    </form>
  );
};
